import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, DollarSign, Tag, ShoppingCart, Award } from "lucide-react";
import { reportService } from "../api/reportService";
import { getDateRangeForPeriod } from "../utils/salesUtils";

const formatCurrency = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "Rp 0";
  return `Rp ${num.toLocaleString("id-ID")}`;
};

export default function FinancialCards({ appliedRange }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadFinancialSummary = async () => {
      try {
        setLoading(true);
        setError("");

        let period;
        if (appliedRange?.startMonth && appliedRange?.endMonth) {
          const [startYear, startMonth] = appliedRange.startMonth.split("-").map(Number);
          const [endYear, endMonth] = appliedRange.endMonth.split("-").map(Number);
          const pad = (num) => String(num).padStart(2, "0");
          const lastDay = new Date(endYear, endMonth, 0).getDate();
          period = {
            start_date: `${startYear}-${pad(startMonth)}-01`,
            end_date: `${endYear}-${pad(endMonth)}-${pad(lastDay)}`,
          };
        } else {
          period = getDateRangeForPeriod("month");
        }

        const response = await reportService.getDashboard({
          ...period,
          pending_limit: 10,
        });

        setSummary(response?.data?.summary || null);
      } catch (err) {
        console.error("Failed to load financial cards:", err);
        setError(err?.message || "Gagal memuat ringkasan finansial");
      } finally {
        setLoading(false);
      }
    };

    loadFinancialSummary();
  }, [appliedRange]);

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user_data") || "{}");
    } catch {
      return {};
    }
  }, []);
  const isAdmin = userData?.is_admin === true;

  const cards = useMemo(
    () => [
      {
        title: "Total Penjualan",
        amount: formatCurrency(summary?.total_sales),
        icon: DollarSign,
        iconBg: "bg-emerald-50",
        iconColor: "text-emerald-600",
        accent: "from-emerald-500/10",
        wave: "text-emerald-500",
        adminOnly: true,
      },
      {
        title: "Total HPP",
        amount: formatCurrency(summary?.total_hpp),
        icon: Tag,
        iconBg: "bg-rose-50",
        iconColor: "text-rose-600",
        accent: "from-rose-500/10",
        wave: "text-rose-500",
        adminOnly: true,
      },
      {
        title: "Total Pengeluaran",
        amount: formatCurrency(summary?.total_expense),
        icon: ShoppingCart,
        iconBg: "bg-amber-50",
        iconColor: "text-amber-600",
        accent: "from-amber-500/10",
        wave: "text-amber-500",
        adminOnly: true,
      },
      {
        title: "Total Keuntungan",
        amount: formatCurrency(summary?.total_profit),
        icon: Award,
        iconBg: "bg-teal-50",
        iconColor: "text-teal-600",
        accent: "from-teal-500/10",
        wave: "text-teal-500",
        adminOnly: true,
      },
    ].filter(card => !card.adminOnly || isAdmin),
    [summary, isAdmin],
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
      {loading && (
        <div className="lg:col-span-4 text-sm text-slate-500 bg-white ring-1 ring-slate-200/70 rounded-xl px-4 py-3 shadow-sm flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Memuat ringkasan finansial...
        </div>
      )}

      {error && !loading && (
        <div className="lg:col-span-4 text-sm text-rose-600 bg-rose-50 ring-1 ring-rose-200 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {cards.map((card, index) => (
        <div
          key={index}
          className="group relative bg-white p-5 rounded-2xl ring-1 ring-slate-200/70 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">
          {/* corner accent gradient */}
          <div
            className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${card.accent} to-transparent pointer-events-none`}
          />

          <div className="relative flex items-center gap-3 mb-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.iconBg} ${card.iconColor} ring-1 ring-slate-100`}>
              <card.icon className="w-5 h-5" />
            </div>
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
              {card.title}
            </p>
          </div>

          <h3 className="relative text-2xl font-bold text-slate-900 tracking-tight mb-1">
            {loading ? "—" : card.amount}
          </h3>

          {/* decorative wave (lightweight) */}
          <div className="absolute bottom-0 left-0 w-full h-10 opacity-[0.08] pointer-events-none">
            <svg
              viewBox="0 0 1440 320"
              className={`fill-current ${card.wave}`}
              preserveAspectRatio="none">
              <path d="M0,224L48,213.3C96,203,192,192,288,197.3C384,203,480,224,576,224C672,224,768,203,864,186.7C960,171,1056,160,1152,165.3C1248,171,1344,192,1392,202.7L1440,213.3L1440,320L0,320Z" />
            </svg>
          </div>
        </div>
      ))}
    </div>
  );
}
